import { supabase } from "../lib/supabase";

export class AuthService {
  constructor() {
    this.currentUser = null;
    this.isInitialized = false;
  }

  async initialize() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      this.currentUser = session?.user || null;
      this.isInitialized = true;

      // Listen for auth changes
      supabase.auth.onAuthStateChange((event, session) => {
        this.currentUser = session?.user || null;
        if (event === "SIGNED_IN") {
          this.handleSignIn(session.user);
        } else if (event === "SIGNED_OUT") {
          this.handleSignOut();
        }
      });

      return this.currentUser;
    } catch (error) {
      console.error("Auth initialization error:", error);
      throw error;
    }
  }

  async upsertUserProfile(userId, userData = {}) {
    const { data, error } = await supabase
      .from("profiles")
      .upsert([
        {
          id: userId,
          email: userData.email,
          name: userData.name,
          // avatar_url: userData.avatar_url,
          updated_at: new Date().toISOString(),
        },
      ])
      .select();

    if (error) {
      console.error("Error upserting profile:", error);
      throw error;
    }

    return data[0];
  }

  async signUp(email, password, userData = {}) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
        },
      });

      if (error) throw error;

      // Create profile after successful sign-up
      if (data.user) {
        await this.upsertUserProfile(data.user?.id, {
          email: data.user.email,
          ...userData,
        });
      }

      return { user: data.user, session: data.session };
    } catch (error) {
      console.error("Sign up error:", error);
      throw error;
    }
  }

  async signIn(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      return { user: data.user, session: data.session };
    } catch (error) {
      console.error("Sign in error:", error);
      throw error;
    }
  }

  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  }

  async resetPassword(email) {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
    } catch (error) {
      console.error("Password reset error:", error);
      throw error;
    }
  }

  async updatePassword(newPassword) {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;
    } catch (error) {
      console.error("Password update error:", error);
      throw error;
    }
  }

  async updateProfile(updates) {
    try {
      // Update user metadata in auth
      const { error: authError } = await supabase.auth.updateUser({
        data: updates,
      });

      if (authError) throw authError;

      // Upsert profile data in database
      if (this.currentUser) {
        const { data, error: profileError } = await supabase
          .from("profiles")
          .upsert({
            id: this.currentUser.id,
            ...updates,
            updated_at: new Date().toISOString(),
          })
          .select();

        if (profileError) throw profileError;
        return data[0];
      }
    } catch (error) {
      console.error("Profile update error:", error);
      throw error;
    }
  }

  getCurrentUser() {
    return this.currentUser;
  }

  isAuthenticated() {
    return !!this.currentUser;
  }

  async handleSignIn(user) {
    try {
      // Upsert profile to ensure it exists and is up to date
      await this.upsertUserProfile(user?.id, {
        email: user.email,
        name: user.user_metadata?.name || user.email.split("@")[0],
      });
    } catch (error) {
      console.error("Error handling sign in:", error);
    }
  }

  handleSignOut() {
    // Clear any local storage or state
    localStorage.removeItem("twinHeartData");
    this.currentUser = null;
  }

  // Social auth methods
  async signInWithGoogle() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Google sign in error:", error);
      throw error;
    }
  }

  async signInWithApple() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "apple",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Apple sign in error:", error);
      throw error;
    }
  }

  // Get user session
  async getSession() {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    } catch (error) {
      console.error("Get session error:", error);
      throw error;
    }
  }

  // Refresh session
  async refreshSession() {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.refreshSession();
      if (error) throw error;
      return session;
    } catch (error) {
      console.error("Refresh session error:", error);
      throw error;
    }
  }
}

export const authService = new AuthService();
